import { useEffect, useState } from "react";
import { Droplets, Loader2, Check } from "lucide-react";
import { portalApi, apiError } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuth";
import { toast } from "sonner";

const FIELDS = [
  ["about", "A little about you", "Tell your cohort who you are — where you're from, your season of life, what you're carrying in…", 3],
  ["likes", "What you love / what fills you", "Long walks, your kids' laughter, worship music, a good book…", 2],
  ["prayer", "What you need prayer for", "Be honest — this is how your cohort will know how to bless you…", 2],
];

// Members must complete their blessing card before entering the portal.
export default function OnboardingBlessing() {
  const { user, refreshUser, logout } = usePortalAuth();
  const [questions, setQuestions] = useState([]);
  const [form, setForm] = useState({ about: "", likes: "", prayer: "", rapid: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    portalApi.get(`/portal/profile/${user.id}`).then((r) => {
      const a = r.data.about || {};
      setQuestions(a.questions || []);
      setForm({ about: a.about || "", likes: a.likes || "", prayer: a.prayer || "", rapid: a.rapid || {} });
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user?.id]);

  const textOk = form.about.trim() && form.likes.trim() && form.prayer.trim();
  const rapidOk = questions.length > 0 && questions.every((q) => (form.rapid[q.id] || "").trim());
  const complete = textOk && rapidOk;
  const answered = questions.filter((q) => (form.rapid[q.id] || "").trim()).length;

  const submit = async () => {
    if (!complete) { toast.error("Please answer everything so your cohort knows how to bless you."); return; }
    setSaving(true);
    try {
      await portalApi.put("/portal/profile/about", form);
      await refreshUser();
      toast.success("Welcome in. Your cohort can see how to bless you now.");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="onboarding-blessing" className="min-h-screen bg-[#F4EFE6] text-[#2A2825]">
      <div className="max-w-2xl mx-auto px-5 md:px-8 py-12 md:py-16">
        <div className="flex items-center gap-3 mb-6">
          <Droplets className="w-6 h-6 text-[#A9724E]" strokeWidth={1.5} />
          <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a]">Before you enter · your blessing card</p>
        </div>
        <div data-testid="onboarding-welcome-banner" className="flex items-center gap-3 rounded-xl border border-[#A9724E]/30 bg-[#EAE3D9]/70 px-4 py-3 mb-8">
          <span className="shrink-0 w-8 h-8 rounded-full bg-[#A9724E] text-[#F4EFE6] flex items-center justify-center"><Check className="w-4 h-4" strokeWidth={2} /></span>
          <p className="text-sm font-body text-[#5E4B3C]">You're signed in{user?.name ? `, ${user.name.split(" ")[0]}` : ""}! Just one step before you enter the portal.</p>
        </div>
        <h1 className="font-serif-display font-light text-4xl md:text-5xl mb-3">First, let your cohort know you.</h1>
        <p className="text-[#5E4B3C] font-body mb-10 leading-relaxed">
          Outpoured is a place to be truly known. Take a few minutes to fill this in — it's how your sisters will pray for you and encourage you. You'll step into the journey the moment it's complete.
        </p>

        {loading ? <p className="text-[#8a7a6a] font-body">Loading…</p> : (
          <div className="space-y-6">
            <div className="bg-white/60 border border-[#E0D8C9] rounded-2xl p-6 space-y-5">
              {FIELDS.map(([k, label, ph, rows]) => (
                <div key={k}>
                  <label className="text-sm font-body text-[#5E4B3C] block mb-1.5">{label} <span className="text-[#A9724E]">*</span></label>
                  <textarea data-testid={`onboarding-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} rows={rows} placeholder={ph}
                    className="w-full bg-white border border-[#D8CEBE] rounded-md px-3 py-2 font-body outline-none focus:border-[#A9724E] resize-none" />
                </div>
              ))}
            </div>

            <div className="bg-white/60 border border-[#E0D8C9] rounded-2xl p-6">
              <p className="text-sm font-body text-[#5E4B3C] mb-1">Rapid fire <span className="text-[#A9724E]">*</span></p>
              <p className="text-xs text-[#8a7a6a] font-body mb-4">All of them — the little things make you, you. ({answered}/{questions.length})</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {questions.map((q) => (
                  <div key={q.id}>
                    <label className="text-xs text-[#8a7a6a] font-body block mb-1">{q.q}</label>
                    <input data-testid={`onboarding-rapid-${q.id}`} value={form.rapid[q.id] || ""} onChange={(e) => setForm({ ...form, rapid: { ...form.rapid, [q.id]: e.target.value } })}
                      className="w-full bg-white border border-[#D8CEBE] rounded-md px-3 py-1.5 text-sm font-body outline-none focus:border-[#A9724E]" />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button data-testid="onboarding-submit" onClick={submit} disabled={saving || !complete}
                className="inline-flex items-center gap-2 rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-7 py-3 text-sm tracking-wide transition-colors disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{saving ? "Entering…" : "Enter the journey"}
              </button>
              {!complete && <span className="text-xs text-[#8a7a6a] font-body">Answer every field to continue.</span>}
              <button onClick={() => { logout(); }} className="ml-auto text-xs text-[#8a7a6a] hover:text-[#A9724E] font-body">Sign out</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
